import { Tab as ChakraTab, TabPanel } from '@chakra-ui/react';
import { SnapSuggestionStatus } from '@snap-types/models/common';
import { QuotePriceMetricsDevViewTable } from '@ui/components/QuotePriceMetricsDevTable';
import { useQuoteLineContext } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineProvider';
import { actions } from '@ui/features/quote-builder/state/reducer';
import {
  selectQuoteLine,
  selectQuoteLineWithLeadTimeIssues,
} from '@ui/features/quote-builder/state/selectors/quote-line';
import { QuoteInfoTab } from '@ui/features/quote-builder/types/state/quote-line';
import { useCurrentUser } from '@ui/hooks/useCurrentUser';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';
import { useState } from 'react';

import { TabComponentType } from './types';

const tab = QuoteInfoTab.MetricsDevView;

function Tab() {
  const dispatch = useDispatch();
  const { isSuperAdmin } = useCurrentUser();
  const { quoteLineUuid } = useQuoteLineContext();

  if (!isSuperAdmin) {
    return <></>;
  }

  function handleTabChange() {
    dispatch(actions.quoteLineInfoTabChanged({ quoteLineUuid, tab }));
  }

  return <ChakraTab onClick={handleTabChange}>Metrics Dev View</ChakraTab>;
}

function MetricsDevViewPanel() {
  const { quoteLineUuid } = useQuoteLineContext();

  const quoteLine = useSelector((state) =>
    selectQuoteLine(state, quoteLineUuid),
  );

  const { quotePrices, quotePriceUuids } = useSelector((state) =>
    selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid),
  );

  // Subscribe to Redux updates because we use optimistic mutations, updating the store state
  // rather than fully re-querying the API for the mutated data
  const automatable = useSelector(
    (state) =>
      selectQuoteLineWithLeadTimeIssues(state, quoteLine.uuid).automatable,
  );
  const leadTimeSuggestionStatus = useSelector(
    (state) =>
      selectQuoteLineWithLeadTimeIssues(state, quoteLine.uuid)
        .leadTimeSuggestionStatus,
  );
  const allPricesAccepted = useSelector(
    (state) =>
      selectQuoteLineWithLeadTimeIssues(state, quoteLine.uuid)
        .suggestedPricesStatus === SnapSuggestionStatus.ACCEPTED,
  );

  const [quotePriceUuid, setQuotePriceUuid] = useState<string>(
    quotePriceUuids[0],
  );

  const quotePriceLines = Object.values(quotePrices).map(
    (quotePrice, index) => {
      return {
        line: index,
        uuid: quotePrice.uuid,
        quantity: quotePrice.quantity,
        suggestedPriceStatus: quotePrice.suggestedPriceStatus,
      };
    },
  );

  const currentQuotePrice = quotePrices[quotePriceUuid];

  if (!currentQuotePrice) {
    // This happens when the currently-viewed quote price is deleted
    setQuotePriceUuid(quotePriceUuids[0]);
  }

  return (
    <QuotePriceMetricsDevViewTable
      currentQuotePrice={currentQuotePrice}
      currentQuoteLine={quoteLine}
      quotePriceLines={quotePriceLines}
      setQuotePriceUuid={setQuotePriceUuid}
      automatable={automatable}
      leadTimeSuggestionStatus={leadTimeSuggestionStatus}
      allPricesAccepted={allPricesAccepted}
    />
  );
}

function Panel() {
  return (
    <TabPanel>
      <MetricsDevViewPanel />
    </TabPanel>
  );
}

export const MetricsDevView = {
  tab,
  Tab,
  Panel,
} satisfies TabComponentType;
